import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { ReactNode } from "react";
import { CostsRequestDto } from "~/components/pages/itemManagement/incidents/create/steps/new-costs/types";
import {
  QueryField,
  QueryPayload,
  QueryStore,
} from "~/components/pages/reports_old/query/types";
import { FindingsRequestDto } from "~/components/shared/findings/types";
import {
  DATE_TIME,
  DATE_TIME_NULLABLE,
  QUERY_FIELDS_STORE,
  QUERY_OPERATORS_STORE,
  QUERY_VALUES_STORE,
} from "~/lib/constants";

import { ReduxState } from "../store";

interface DetailedProps {
  title: string;
  enableChildView?: boolean;
  // childContent?: React.ReactNode | null;
  type?:
    | "Findings"
    | "Costs"
    | "Action"
    | "SaveFindings"
    | "SaveCosts"
    | "LostTime";
  modelId?: string;
  modelType?: string;
  action?: ReactNode;
  itemId?: string;
}
export interface CommonState {
  searchInput: string;
  employeeIds: string[];
  employeeId: string;
  itemId: string;
  certificationStatus: string;
  itemManagementStatuses: string;
  itemType: string;
  site: string;
  bulkDeleteIds: string[];
  questionType: string;
  formTypes: string;
  queryFields: QueryStore;
  queryOperators: QueryStore;
  queryValues: QueryStore;
  triggerReload?: boolean;
  detailedPage?: DetailedProps | null;
  globalFindings?: FindingsRequestDto[];
  globalCostings?: CostsRequestDto[];
}

const initialState: CommonState = {
  searchInput: "",
  employeeIds: [],
  bulkDeleteIds: [],
  globalFindings: [],
  employeeId: "",
  itemId: "",
  certificationStatus: "",
  itemManagementStatuses: "",
  itemType: "",
  site: "",
  questionType: "",
  formTypes: "",
  queryFields: QUERY_FIELDS_STORE,
  queryOperators: QUERY_OPERATORS_STORE,
  queryValues: QUERY_VALUES_STORE,
  triggerReload: false,
  detailedPage: null,
};

export const commonSlice = createSlice({
  name: "common",
  initialState,
  reducers: {
    setQueryField: (state, action: PayloadAction<QueryPayload, string>) => {
      const { parameterType, data } = action.payload;

      const fieldsArray = data?.map((item) => {
        return {
          label: item?.displayName,
          value: item?.name,
          dateTime:
            item?.fieldType === (DATE_TIME || DATE_TIME_NULLABLE)
              ? item?.fieldType
              : null,
          fieldType: item?.fieldType,
          relatedEntityType: item?.relatedEntityType,
        } as QueryField;
      });
      const operatorsArray = data?.map((item) => {
        return {
          label: item?.displayName,
          operators: item?.operators,
        };
      });
      const valuesArray = data?.map((item) => {
        return {
          label: item?.displayName,
          relatedEntityType: item?.relatedEntityType,
          values: null,
        };
      });
      state.queryFields[parameterType] = fieldsArray;
      state.queryOperators[parameterType] = operatorsArray;
      state.queryValues[parameterType] = valuesArray;
    },
    setSearchValue: (state, action: PayloadAction<string, string>) => {
      state.searchInput = action.payload;
    },
    setAddNewGlobalFindings: (
      state,
      action: PayloadAction<FindingsRequestDto[], string>,
    ) => {
      state.globalFindings = [
        ...(state.globalFindings || []),
        ...action.payload,
      ];
    },
    setAddNewGlobalFinding: (
      state,
      action: PayloadAction<FindingsRequestDto, string>, // a single finding
    ) => {
      state.globalFindings = [...(state.globalFindings || []), action.payload];
    },
    removeGlobalFindings: (
      state,
      action: PayloadAction<string[], string>, // array of IDs to remove
    ) => {
      const idsToRemove = new Set(action.payload);
      state.globalFindings = (state.globalFindings || []).filter(
        (item) => item && !idsToRemove.has(item?.id as string),
      );
    },

    removeGlobalFindingById: (
      state,
      action: PayloadAction<string, string>, // single ID
    ) => {
      const idToRemove = action.payload;
      state.globalFindings = (state.globalFindings || []).filter(
        (item) => item && item.id !== idToRemove,
      );
    },
    clearGlobalFindings: (state) => {
      state.globalFindings = [];
    },
    updateGlobalFindingById: (
      state,
      action: PayloadAction<FindingsRequestDto, string>, // updated item with same id
    ) => {
      const updatedItem = action.payload;
      state.globalFindings = (state.globalFindings || []).map((item) =>
        item.id === updatedItem.id ? updatedItem : item,
      );
    },
    setAddNewGlobalCosts: (
      state,
      action: PayloadAction<CostsRequestDto[], string>,
    ) => {
      state.globalCostings = [
        ...(state.globalCostings || []),
        ...action.payload,
      ];
    },
    setAddNewGlobalCost: (
      state,
      action: PayloadAction<CostsRequestDto, string>, // a single finding
    ) => {
      state.globalCostings = [...(state.globalCostings || []), action.payload];
    },
    removeGlobalCosts: (
      state,
      action: PayloadAction<string[], string>, // array of IDs to remove
    ) => {
      const idsToRemove = new Set(action.payload);
      state.globalFindings = (state.globalFindings || []).filter(
        (item) => item && !idsToRemove.has(item?.id as string),
      );
    },
    removeGlobalCostById: (
      state,
      action: PayloadAction<string, string>, // single ID
    ) => {
      const idToRemove = action.payload;
      state.globalFindings = (state.globalFindings || []).filter(
        (item) => item && item.id !== idToRemove,
      );
    },
    clearGlobalCosts: (state) => {
      state.globalCostings = [];
    },
    updateGlobalCostById: (
      state,
      action: PayloadAction<CostsRequestDto, string>, // updated item with same id
    ) => {
      const updatedItem = action.payload;
      state.globalCostings = (state.globalCostings || []).map((item) =>
        item.id === updatedItem.id ? updatedItem : item,
      );
    },

    selectEmployees: (state, action: PayloadAction<string[], string>) => {
      state.employeeIds = action.payload;
    },
    selectBulkDeleteItems: (state, action: PayloadAction<string[], string>) => {
      state.bulkDeleteIds = action.payload;
    },
    setTriggerReload: (state) => {
      state.triggerReload = true;
    },
    unSetTriggerReload: (state) => {
      state.triggerReload = false;
    },
    toggleDetailedPage: (
      state,
      action: PayloadAction<DetailedProps | null, string>,
    ) => {
      state.detailedPage = action.payload;
    },
    selectEmployee: (state, action: PayloadAction<string, string>) => {
      state.employeeId = action.payload;
    },
    selectItemId: (state, action: PayloadAction<string, string>) => {
      state.itemId = action.payload;
    },
    selectCertificationStatus: (
      state,
      action: PayloadAction<string, string>,
    ) => {
      state.certificationStatus = action.payload;
    },
    selectItemManagementStatuses: (
      state,
      action: PayloadAction<string, string>,
    ) => {
      state.itemManagementStatuses = action.payload;
    },
    selectItemType: (state, action: PayloadAction<string, string>) => {
      state.itemType = action.payload;
    },
    selectSiteType: (state, action: PayloadAction<string, string>) => {
      state.site = action.payload;
    },
    selectQuestionType: (state, action: PayloadAction<string, string>) => {
      state.questionType = action.payload;
    },
    updateQuestionTypes: (state, action: PayloadAction<string>) => {
      state.questionType = action.payload;
    },
    selectFormTypes: (state, action: PayloadAction<string, string>) => {
      state.formTypes = action.payload;
    },
    updateFormTypes: (state, action: PayloadAction<string>) => {
      state.formTypes = action.payload;
    },
    resetEmployees: (state) => {
      state.employeeIds = [];
    },
    resetBulkDeletedItems: (state) => {
      state.employeeIds = [];
    },
    resetEmployee: (state) => {
      state.employeeId = "";
    },
    resetItemId: (state) => {
      state.itemId = "";
    },
    resetCertificationStatus: (state) => {
      state.certificationStatus = "";
    },
    resetItemManagementStatuses: (state) => {
      state.itemManagementStatuses = "";
    },
    resetItemType: (state) => {
      state.itemType = "";
    },
    resetSite: (state) => {
      state.site = "";
    },
    resetQuestionType: (state) => {
      state.questionType = "";
    },
    resetFormType: (state) => {
      state.formTypes = "";
    },

    clearSearchValue: (state) => {
      state.searchInput = "";
    },
  },
});

function createSelector<T>(fn: (d: ReduxState["common"]) => T) {
  return ({ common }: ReduxState) => fn(common);
}

export const common = createSelector((common: CommonState) => common);
export const searchValue = createSelector(
  (common: CommonState) => common.searchInput,
);
export const commonActions = commonSlice.actions;

const commonReducer = commonSlice.reducer;

export default commonReducer;
